import * as React from 'react';

import {
  ReactFCC,
  IconRegularChevronLeft,
  IconRegularChevronRight,
} from '@notino/react-styleguide';

import {
  AnimateMountUnmount,
  mountClassName,
  unmountClassName,
} from '@components/Animate';
import { useArrows, Fade, Arrow } from '@hooks/useArrows';

import {
  ItemList,
  Header,
  Footer,
  Container,
  SubHeader,
  ScrollWrapper,
} from './styled';

interface IStepContainerProps {
  header?: React.ReactNode;
  subheader?: React.ReactNode;
  footer?: React.ReactNode;
  unmounting: boolean;
  horizontalScroll?: boolean;
  containerJustifyCenter?: boolean;
}

export const StepContainer: ReactFCC<IStepContainerProps> = ({
  header,
  subheader,
  children,
  unmounting,
  footer,
  containerJustifyCenter = false,
  horizontalScroll = true,
}) => {
  const { containerRef, scrollTo, showLeftArrow, showRightArrow } = useArrows(
    [],
    { scrollAmount: 600 }
  );

  const scrollIntoViewOnMount = React.useCallback((node: HTMLDivElement) => {
    node?.scrollIntoView();
  }, []);

  const animateMountUnmountClassName = unmounting
    ? unmountClassName
    : mountClassName;

  return (
    <Container
      ref={scrollIntoViewOnMount}
      justifyCenter={containerJustifyCenter}
    >
      {header && (
        <AnimateMountUnmount
          unmounting={unmounting}
          delay={200}
          className={animateMountUnmountClassName}
        >
          <Header aria-live="polite">
            {header}
            {subheader && <SubHeader>{subheader}</SubHeader>}
          </Header>
        </AnimateMountUnmount>
      )}

      <AnimateMountUnmount
        unmounting={unmounting}
        delay={500}
        className={animateMountUnmountClassName}
      >
        <ItemList>
          {showLeftArrow && (
            <>
              <Arrow
                position="left"
                onClick={() => scrollTo('left')}
                role="button"
              >
                <IconRegularChevronLeft
                  color="icon.primary"
                  height="1.25rem"
                  width="1.25rem"
                />
              </Arrow>

              <Fade position="left" />
            </>
          )}

          {horizontalScroll ? (
            <ScrollWrapper
              ref={containerRef as React.RefObject<HTMLDivElement>}
            >
              {children}
            </ScrollWrapper>
          ) : (
            children
          )}

          {showRightArrow && (
            <>
              <Arrow
                position="right"
                onClick={() => scrollTo('right')}
                role="button"
              >
                <IconRegularChevronRight
                  color="icon.primary"
                  height="1.25rem"
                  width="1.25rem"
                />
              </Arrow>

              <Fade position="right" />
            </>
          )}
        </ItemList>
      </AnimateMountUnmount>

      {footer && (
        <AnimateMountUnmount unmounting={unmounting} delay={500}>
          <Footer>{footer}</Footer>
        </AnimateMountUnmount>
      )}
    </Container>
  );
};
